import { useCallback, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { getUser, localStorageHelper } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  ChecklistField,
  ConfirmationModal,
  styles,
  Text,
  Truncated,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { hierarchyBorderStyle } from '../constants';
import { useLocationPickerDemoUiEnabled } from '../demo-ui-config';
import { useLocationPicker } from '../store';
import { LocationPickerTrackingIds } from '../tracking-ids';
import { DemoExpirationChip } from './demo-expiration-chip';
import { LocationRadioField } from './shared/location-radio';

const handleIgnoreUpdateLocationGroupModal = (userId: string) => {
  const ignoreUpdateLocationGroupModal = localStorageHelper.get<boolean>(`ignoreUpdateLocationGroupModal-${userId}`);
  return !ignoreUpdateLocationGroupModal;
};

const handleSetIgnoreUpdateLocationGroupModal = (userId: string) => {
  localStorageHelper.create(`ignoreUpdateLocationGroupModal-${userId}`, 'true');
};

const resultContainerStyle = css`
  padding: ${theme.spacing(1)};
  padding-bottom: 0;
  content-visibility: auto;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${theme.colors.neutral10};
  }
`;

const sectionContainerStyle = css`
  padding: ${theme.spacing(1)};
  border-bottom: 1px solid ${theme.colors.neutral10};
  content-visibility: auto;
`;

const organizationNameStyle = css`
  margin-bottom: ${theme.spacing(1)};
  content-visibility: auto;
`;

const locationGroupNameStyle = css`
  margin-bottom: ${theme.spacing(1)};
  content-visibility: auto;
`;

const radioContainerStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
`;

const radioContentContainerStyle = css`
  margin-top: ${theme.spacing(-0.25)};
  min-width: 0;
  width: 100%;
`;

const locationsContainerStyle = css`
  margin-top: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1)};
  margin-left: ${theme.spacing(1)};
`;

const groupItemContainerStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};
`;

const groupsContainerStyle = css`
  margin-left: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1)};
`;

const blockTextStyle = css`
  display: block;
`;

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

type LocationSearchResultsProps = React.PropsWithChildren<{
  groupResults: LocationGroupResult;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  allowCrossGroupSelection?: boolean;
}>;

interface GroupSearchResultsProps {
  numberOfLocations: number;
  organizationName: string;
  locationGroupName: string;
  organizationId: string;
  locationGroupId: string;
  onSelected: () => void;
  locations?: Array<string>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  allowCrossGroupSelection?: boolean;
}

interface LocationGroupItemProps {
  locationGroupName: string;
  numberOfLocations: number;
  organizationId: string;
  locationGroupId: string;
  locations?: Array<string>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  allowCrossGroupSelection?: boolean;
}

interface OrgSearchResultsProps {
  numberOfGroups: number;
  numberOfLocations: number;
  organizationName: string;
  organizationId: string;
  locationGroups?: Array<{
    id: string;
    name: string;
    numberOfLocations: number;
    locations?: Array<string>;
  }>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  allowCrossGroupSelection?: boolean;
}

export const LocationSearchResults = ({
  groupResults: {
    orgName: organizationName,
    groupName: locationGroupName,
    orgId: organizationId,
    groupId: locationGroupId,
    locations: locationResults,
  },
  renderLocationInfo,
  allowCrossGroupSelection = false,
}: LocationSearchResultsProps) => {
  const showDemoUi = useLocationPickerDemoUiEnabled();
  const {
    inFlightLocationIds,
    inFlightGroupId,
    setInFlightGroupId,
    setInFlightOrganizationId,
    setInFlightLocationIds,
    isSimpleView,
    hasSingleOrganization,
    hasSingleLocationGroup,
  } = useLocationPicker([
    'inFlightLocationIds',
    'inFlightGroupId',
    'setInFlightGroupId',
    'setInFlightOrganizationId',
    'setInFlightLocationIds',
    'isSimpleView',
    'hasSingleOrganization',
    'hasSingleLocationGroup',
  ]);
  const { modalProps, openModal } = useModalControl();
  const inFlightRef = useRef<{ name: string; value: string }>();
  const user = getUser();
  const { t } = useTranslation('location-picker');
  const [isModalVisible, setIsModalVisible] = useState(() => handleIgnoreUpdateLocationGroupModal(user?.userID || ''));
  const showUpdateLocationGroupModal = !isSimpleView && isModalVisible;

  const locationIdsInGroup = useMemo(() => locationResults.map((location) => location.id), [locationResults]);
  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);

  const locationSearchResultsField = useFormField(
    {
      type: 'checklist',
      value: allowCrossGroupSelection
        ? locationIdsInGroup.filter((id) => inFlightSet.has(id))
        : locationGroupId === inFlightGroupId
        ? inFlightLocationIds
        : [],
    },
    [allowCrossGroupSelection, locationIdsInGroup, inFlightSet, inFlightGroupId]
  );

  const ignoreModalChecklist = useFormField({
    type: 'checklist',
  });

  const handleLocationChange = useCallback(() => {
    locationSearchResultsField.onChange(inFlightRef.current);
    setInFlightLocationIds(
      allowCrossGroupSelection
        ? [...new Set([...inFlightLocationIds, inFlightRef?.current?.value ?? ''])]
        : [inFlightRef?.current?.value ?? '']
    );
    setInFlightGroupId(locationGroupId);
    setInFlightOrganizationId(organizationId);
  }, [
    locationSearchResultsField,
    allowCrossGroupSelection,
    inFlightLocationIds,
    setInFlightLocationIds,
    setInFlightGroupId,
    setInFlightOrganizationId,
    locationGroupId,
    organizationId,
  ]);

  const handleChecklistChange = useCallback(
    (e: any) => {
      const newValue = e?.value;

      if (allowCrossGroupSelection) {
        setInFlightOrganizationId(organizationId);
        const selectedLocationsInGroup = locationIdsInGroup.filter((locationId) => inFlightSet.has(locationId));
        const nextLocations = selectedLocationsInGroup.includes(newValue)
          ? selectedLocationsInGroup.filter((locationId) => locationId !== newValue)
          : [...selectedLocationsInGroup, newValue];
        setInFlightLocationIds(nextLocations);
        setInFlightGroupId(nextLocations.length === 0 ? '' : locationGroupId);
        return;
      }

      const isValueSelected = locationSearchResultsField.value.includes(newValue);

      if (isValueSelected) {
        const nextLocations = locationSearchResultsField.value.filter((location) => location !== newValue);
        setInFlightLocationIds(nextLocations);
        if (nextLocations.length === 0) setInFlightGroupId('');
        return;
      }

      const isChangingGroups = inFlightGroupId !== locationGroupId;

      if (!isChangingGroups) {
        setInFlightLocationIds([...locationSearchResultsField.value, newValue]);
        return;
      }

      inFlightRef.current = e;

      if (!showUpdateLocationGroupModal) {
        handleLocationChange();
      } else {
        openModal();
      }
    },
    [
      allowCrossGroupSelection,
      inFlightSet,
      locationGroupId,
      locationIdsInGroup,
      organizationId,
      locationSearchResultsField,
      inFlightGroupId,
      setInFlightGroupId,
      setInFlightOrganizationId,
      setInFlightLocationIds,
      openModal,
      showUpdateLocationGroupModal,
      handleLocationChange,
    ]
  );

  const handleConfirm = useCallback(() => {
    handleLocationChange();

    if (!!ignoreModalChecklist.value.length) {
      handleSetIgnoreUpdateLocationGroupModal(user?.userID || '');
      setIsModalVisible(false);
    }
  }, [handleLocationChange, ignoreModalChecklist.value, user?.userID]);

  const handleCancel = useCallback(() => {
    inFlightRef.current = undefined;
  }, []);

  return (
    <div css={resultContainerStyle}>
      {!hasSingleOrganization && (
        <Text size='small' color='subdued' css={organizationNameStyle}>
          {organizationName} {showDemoUi ? <DemoExpirationChip orgId={organizationId} /> : null}
        </Text>
      )}
      {!hasSingleLocationGroup && (
        <Text
          size='small'
          css={[!hasSingleOrganization && hierarchyBorderStyle(), locationGroupNameStyle]}
          color='light'
        >
          {locationGroupName}
        </Text>
      )}

      <div
        css={[
          hasSingleLocationGroup
            ? css`
                margin-top: ${theme.spacing(1)};
              `
            : hierarchyBorderStyle(!hasSingleOrganization ? 2 : 1),
        ]}
      >
        <ChecklistField
          {...locationSearchResultsField}
          onChange={handleChecklistChange}
          label=''
          showLabel={false}
          css={css`
            > label {
              min-width: 0;
            }
          `}
          name='location-search-results'
        >
          {locationResults.map((location) => (
            <ChecklistField.Option key={location.id} name={location.id}>
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  gap: ${theme.spacing(1)};
                  width: 100%;
                  min-width: 0;
                `}
              >
                <span css={[styles.truncate]}> {location.name} </span>
                <span> {renderLocationInfo?.(location.id)}</span>
              </span>
            </ChecklistField.Option>
          ))}
        </ChecklistField>
      </div>
      <ConfirmationModal
        title='Update Location Group'
        message={
          <>
            <Text>
              {t(
                `This location is associated with a different location group from the one you're currently viewing. Selecting it will update your current selection.`
              )}
            </Text>
            <ChecklistField {...ignoreModalChecklist} label='' name='ignore-update-location-group-modal'>
              <ChecklistField.Option
                css={css`
                  padding: 0;
                  margin: 0;
                `}
                name='ignore-update-location-group-modal-box'
              >
                <Text color='light'>{t(`Don't show this again`)}</Text>
              </ChecklistField.Option>
            </ChecklistField>
          </>
        }
        {...modalProps}
        confirmLabel='Update Selection'
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};

export const GroupSearchResults = ({
  organizationName,
  locationGroupName,
  numberOfLocations,
  organizationId,
  locationGroupId,
  onSelected,
  locations = [],
  getScopeName = () => '',
  allowCrossGroupSelection = false,
}: GroupSearchResultsProps) => {
  const showDemoUi = useLocationPickerDemoUiEnabled();
  const {
    setOrganizationViewId,
    setInFlightOrganizationId,
    setInFlightGroupId,
    inFlightGroupId,
    inFlightLocationIds,
    setInFlightLocationIds,
    hasSingleOrganization,
  } = useLocationPicker([
    'setOrganizationViewId',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'setInFlightLocationIds',
    'hasSingleOrganization',
  ]);

  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);
  const hasSelectedLocationInGroup = locations.some((id) => inFlightSet.has(id));
  const isGroupSelected = allowCrossGroupSelection
    ? hasSelectedLocationInGroup
    : inFlightGroupId
    ? inFlightGroupId === locationGroupId
    : hasSelectedLocationInGroup;

  const locationChecklistField = useFormField(
    {
      type: 'checklist',
      value: allowCrossGroupSelection
        ? locations.filter((id) => inFlightSet.has(id))
        : locationGroupId === inFlightGroupId
        ? inFlightLocationIds
        : [],
    },
    [allowCrossGroupSelection, locations, inFlightSet, inFlightGroupId]
  );

  const handleRadioSelected = useCallback(() => {
    setOrganizationViewId(organizationId);
    setInFlightOrganizationId(organizationId);

    if (allowCrossGroupSelection) {
      const selectedLocationsInGroup = locations.filter((locationId) => inFlightSet.has(locationId));
      const nextLocations = hasSelectedLocationInGroup ? selectedLocationsInGroup : locations;

      setInFlightLocationIds(nextLocations);
      setInFlightGroupId(nextLocations.length === 0 ? '' : locationGroupId);
      return;
    }

    if (isGroupSelected) {
      return;
    }

    setInFlightGroupId(locationGroupId);

    if (locations.length > 0) {
      setInFlightLocationIds(locations);
    }

    onSelected();
  }, [
    allowCrossGroupSelection,
    setOrganizationViewId,
    setInFlightOrganizationId,
    setInFlightGroupId,
    setInFlightLocationIds,
    organizationId,
    locationGroupId,
    hasSelectedLocationInGroup,
    isGroupSelected,
    inFlightSet,
    locations,
    onSelected,
  ]);

  const handleChecklistChange = useCallback(
    (e: any) => {
      const newValue = e?.value;
      if (allowCrossGroupSelection) {
        const selectedLocationsInGroup = locations.filter((locationId) => inFlightSet.has(locationId));
        const nextLocations = selectedLocationsInGroup.includes(newValue)
          ? selectedLocationsInGroup.filter((locationId) => locationId !== newValue)
          : [...selectedLocationsInGroup, newValue];
        setInFlightLocationIds(nextLocations);
        setInFlightGroupId(nextLocations.length === 0 ? '' : locationGroupId);
        return;
      }
      if (locationChecklistField.value.includes(newValue)) {
        const nextLocations = locationChecklistField.value.filter((location) => location !== newValue);
        setInFlightLocationIds(nextLocations);
        if (nextLocations.length === 0) setInFlightGroupId('');
      } else {
        setInFlightLocationIds([...locationChecklistField.value, newValue]);
      }
    },
    [
      allowCrossGroupSelection,
      inFlightSet,
      locationGroupId,
      locations,
      locationChecklistField,
      setInFlightGroupId,
      setInFlightLocationIds,
    ]
  );

  return (
    <div css={sectionContainerStyle}>
      {!hasSingleOrganization && (
        <Text size='small' color='subdued' css={organizationNameStyle}>
          {organizationName} {showDemoUi ? <DemoExpirationChip orgId={organizationId} /> : null}
        </Text>
      )}
      <div
        css={[
          !hasSingleOrganization && hierarchyBorderStyle(),
          hasSingleOrganization &&
            css`
              margin-top: ${theme.spacing(1)};
            `,
          radioContainerStyle,
        ]}
      >
        <LocationRadioField
          id={`location-group-search-${locationGroupId}`}
          isSelected={isGroupSelected}
          onSelected={handleRadioSelected}
          trackingId={LocationPickerTrackingIds.searchGroup}
        />
        <div css={[radioContentContainerStyle]}>
          <Text
            css={css`
              display: flex;
              padding-right: ${theme.spacing(2)};
            `}
          >
            <Truncated
              css={css`
                margin-right: ${theme.spacing(2)};
              `}
              as='span'
            >
              {locationGroupName}
            </Truncated>
            {isGroupSelected && locations.length > 0 && (
              <SelectDeselect
                groupId={locationGroupId}
                groupLocations={locations}
                selectedGroupLocations={
                  allowCrossGroupSelection ? locations.filter((id) => inFlightSet.has(id)) : inFlightLocationIds
                }
                allowCrossGroupSelection={allowCrossGroupSelection}
              />
            )}
          </Text>
          {!isGroupSelected && (
            <Text as='span' size='small' color='subdued'>
              {numberOfLocations} Locations
            </Text>
          )}
        </div>
      </div>
      {isGroupSelected && locations.length === 0 && <LocationGroupEmptyContent marginLeft={6} />}
      {isGroupSelected && locations.length > 0 && (
        <div css={locationsContainerStyle}>
          <div css={hierarchyBorderStyle(!hasSingleOrganization ? 2 : 1)}>
            <ChecklistField
              {...locationChecklistField}
              onChange={handleChecklistChange}
              label=''
              showLabel={false}
              name={`location-list-group-${locationGroupId}`}
            >
              {locations.map((locationId) => (
                <ChecklistField.Option key={locationId} name={locationId}>
                  {getScopeName ? getScopeName(locationId, 'location') : locationId}
                </ChecklistField.Option>
              ))}
            </ChecklistField>
          </div>
        </div>
      )}
    </div>
  );
};

const LocationGroupItem = ({
  locationGroupName,
  numberOfLocations,
  organizationId,
  locationGroupId,
  locations = [],
  getScopeName = () => '',
  allowCrossGroupSelection = false,
}: LocationGroupItemProps) => {
  const {
    setInFlightOrganizationId,
    setInFlightGroupId,
    inFlightGroupId,
    setInFlightLocationIds,
    inFlightLocationIds,
  } = useLocationPicker([
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'inFlightGroupId',
    'setInFlightLocationIds',
    'inFlightLocationIds',
  ]);

  const inFlightSet = useMemo(() => new Set(inFlightLocationIds), [inFlightLocationIds]);

  const locationChecklistField = useFormField(
    {
      type: 'checklist',
      value: allowCrossGroupSelection
        ? locations.filter((id) => inFlightSet.has(id))
        : locationGroupId === inFlightGroupId
        ? inFlightLocationIds
        : [],
    },
    [allowCrossGroupSelection, locations, inFlightSet, inFlightGroupId]
  );

  const hasSelectedLocationInGroup = locations.some((id) => inFlightSet.has(id));
  const isGroupSelected = allowCrossGroupSelection
    ? hasSelectedLocationInGroup
    : inFlightGroupId
    ? inFlightGroupId === locationGroupId
    : hasSelectedLocationInGroup;

  const handleRadioSelected = useCallback(() => {
    setInFlightOrganizationId(organizationId);

    if (allowCrossGroupSelection) {
      const selectedLocationsInGroup = locations.filter((locationId) => inFlightSet.has(locationId));
      const nextLocations = hasSelectedLocationInGroup ? selectedLocationsInGroup : locations;

      setInFlightLocationIds(nextLocations);
      setInFlightGroupId(nextLocations.length === 0 ? '' : locationGroupId);
      return;
    }

    if (isGroupSelected) {
      return;
    }

    setInFlightGroupId(locationGroupId);

    if (locations.length > 0) {
      setInFlightLocationIds(locations);
    }
  }, [
    allowCrossGroupSelection,
    setInFlightOrganizationId,
    setInFlightGroupId,
    setInFlightLocationIds,
    organizationId,
    locationGroupId,
    hasSelectedLocationInGroup,
    inFlightSet,
    isGroupSelected,
    locations,
  ]);

  const handleChecklistChange = useCallback(
    (e: any) => {
      const newValue = e?.value;
      if (allowCrossGroupSelection) {
        const selectedLocationsInGroup = locations.filter((locationId) => inFlightSet.has(locationId));
        const nextLocations = selectedLocationsInGroup.includes(newValue)
          ? selectedLocationsInGroup.filter((locationId) => locationId !== newValue)
          : [...selectedLocationsInGroup, newValue];
        setInFlightLocationIds(nextLocations);
        setInFlightGroupId(nextLocations.length === 0 ? '' : locationGroupId);
        return;
      }
      if (locationChecklistField.value.includes(newValue)) {
        const nextLocations = locationChecklistField.value.filter((location) => location !== newValue);
        setInFlightLocationIds(nextLocations);
        if (nextLocations.length === 0) setInFlightGroupId('');
      } else {
        setInFlightLocationIds([...locationChecklistField.value, newValue]);
      }
    },
    [
      allowCrossGroupSelection,
      inFlightSet,
      locationGroupId,
      locations,
      locationChecklistField,
      setInFlightGroupId,
      setInFlightLocationIds,
    ]
  );

  return (
    <>
      <div css={[hierarchyBorderStyle(2), groupItemContainerStyle]}>
        <LocationRadioField
          id={`location-group-item-${locationGroupId}`}
          isSelected={isGroupSelected}
          onSelected={handleRadioSelected}
        />
        <div css={radioContentContainerStyle}>
          <Text
            css={css`
              display: flex;
            `}
          >
            <Truncated
              css={css`
                margin-right: ${theme.spacing(2)};
              `}
              as='span'
            >
              {locationGroupName}
            </Truncated>
            {isGroupSelected && locations.length > 0 && (
              <SelectDeselect
                groupId={locationGroupId}
                groupLocations={locations}
                selectedGroupLocations={
                  allowCrossGroupSelection ? locations.filter((id) => inFlightSet.has(id)) : inFlightLocationIds
                }
                allowCrossGroupSelection={allowCrossGroupSelection}
              />
            )}
          </Text>
          {!isGroupSelected && (
            <Text as='span' size='small' color='subdued'>
              {numberOfLocations} Locations
            </Text>
          )}
        </div>
      </div>
      {isGroupSelected && locations.length === 0 && <LocationGroupEmptyContent marginLeft={8.5} />}
      {isGroupSelected && locations.length > 0 && (
        <div css={locationsContainerStyle}>
          <div css={hierarchyBorderStyle(3)}>
            <ChecklistField
              {...locationChecklistField}
              onChange={handleChecklistChange}
              label=''
              showLabel={false}
              name={`location-list-${locationGroupId}`}
            >
              {locations.map((locationId) => (
                <ChecklistField.Option key={locationId} name={locationId}>
                  {getScopeName ? getScopeName(locationId, 'location') : locationId}
                </ChecklistField.Option>
              ))}
            </ChecklistField>
          </div>
        </div>
      )}
    </>
  );
};

export const OrgSearchResults = ({
  organizationName,
  numberOfLocations,
  numberOfGroups,
  organizationId,
  locationGroups = [],
  getScopeName,
  allowCrossGroupSelection = false,
}: OrgSearchResultsProps) => {
  const showDemoUi = useLocationPickerDemoUiEnabled();
  const {
    setOrganizationViewId,
    inFlightOrganizationId,
    setInFlightOrganizationId,
    setInFlightGroupId,
    setInFlightLocationIds,
  } = useLocationPicker([
    'setOrganizationViewId',
    'inFlightOrganizationId',
    'setInFlightOrganizationId',
    'setInFlightGroupId',
    'setInFlightLocationIds',
  ]);

  const isOrganizationSelected = inFlightOrganizationId === organizationId;

  const handleRadioSelected = useCallback(() => {
    setOrganizationViewId(organizationId);
    setInFlightOrganizationId(organizationId);
    setInFlightGroupId('');
    setInFlightLocationIds([]);
  }, [setOrganizationViewId, setInFlightOrganizationId, setInFlightGroupId, setInFlightLocationIds, organizationId]);

  return (
    <div css={sectionContainerStyle}>
      <Text size='small' color='subdued' css={organizationNameStyle}>
        {organizationName} {showDemoUi ? <DemoExpirationChip orgId={organizationId} /> : null}
      </Text>
      <div css={[hierarchyBorderStyle(), radioContainerStyle]}>
        <LocationRadioField
          isSelected={isOrganizationSelected}
          id={`organization-search-${organizationId}`}
          onSelected={handleRadioSelected}
        />
        <div css={radioContentContainerStyle}>
          <Text>{organizationName}</Text>
          {!isOrganizationSelected && (
            <>
              <Text as='span' css={blockTextStyle} size='small' color='light'>
                {numberOfGroups} Groups
              </Text>
              <Text as='span' css={blockTextStyle} size='small' color='subdued'>
                {numberOfLocations} Locations
              </Text>
            </>
          )}
        </div>
      </div>
      {isOrganizationSelected && locationGroups.length > 0 && (
        <div css={groupsContainerStyle}>
          {locationGroups.map((group) => (
            <LocationGroupItem
              key={group.id}
              locationGroupName={group.name}
              numberOfLocations={group.numberOfLocations}
              organizationId={organizationId}
              locationGroupId={group.id}
              locations={group.locations}
              getScopeName={getScopeName}
              allowCrossGroupSelection={allowCrossGroupSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LocationGroupEmptyContent = ({ marginLeft = 1 }: { marginLeft?: number }) => {
  return (
    <Text
      size='small'
      color='subdued'
      css={css`
        margin-top: ${theme.spacing(1)};
        border-radius: ${theme.borderRadius.small};
      `}
      style={{
        marginLeft: theme.spacing(marginLeft),
      }}
    >
      This location group contains locations that you are not authorized to access. To manage your organization's
      settings, click <strong>'Apply'</strong>.
    </Text>
  );
};

type SelectDeselectProps = {
  selectedGroupLocations: string[];
  groupLocations: string[];
  groupId: string;
  allowCrossGroupSelection?: boolean;
};

const SelectDeselect = ({
  selectedGroupLocations,
  groupLocations,
  groupId,
  allowCrossGroupSelection = false,
}: SelectDeselectProps) => {
  const { t } = useTranslation();
  const {
    setInFlightGroupId,
    setInFlightLocationIds,
    inFlightLocationIds,
    organizationViewId,
    setInFlightOrganizationId,
  } = useLocationPicker([
    'setInFlightGroupId',
    'setInFlightLocationIds',
    'inFlightLocationIds',
    'organizationViewId',
    'setInFlightOrganizationId',
  ]);

  const groupSet = useMemo(() => new Set(groupLocations), [groupLocations]);

  const handleDeselectAll = () => {
    setInFlightGroupId(allowCrossGroupSelection ? '' : groupId);
    setInFlightLocationIds(allowCrossGroupSelection ? inFlightLocationIds.filter((id) => !groupSet.has(id)) : []);
  };

  const handleSelectAll = () => {
    setInFlightGroupId(groupId);
    setInFlightLocationIds(groupLocations);
    setInFlightOrganizationId(organizationViewId ?? '');
  };

  if (selectedGroupLocations.length === groupLocations.length) {
    return (
      <Button
        css={css`
          margin-left: auto;
          margin-top: ${theme.spacing(-0.6)};
        `}
        onClick={handleDeselectAll}
        variant='tertiary'
        size='small'
        trackingId={LocationPickerTrackingIds.deselectAll('search-results')}
      >
        {t('Deselect All')}
      </Button>
    );
  }

  return (
    <Button
      css={css`
        margin-left: auto;
        margin-top: ${theme.spacing(-0.6)};
      `}
      onClick={handleSelectAll}
      variant='tertiary'
      trackingId={LocationPickerTrackingIds.selectAll('search-results')}
    >
      {t('Select All')}
    </Button>
  );
};
